import { Suspense, useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import type { RiskMapTab } from '@kirin/api-client/contracts/ai-risk-map';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { TimeRangeSelector } from '@/components/TimeRangeSelector';
import { quantiseRiskMapRange } from '@/hooks/api/useAiRiskMap';
import { useOrganization } from '@/hooks/api/useOrganizations';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useDashboardTimeFilter } from '../useDashboardTimeFilter';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { DashboardErrorBoundary } from '../DashboardErrorBoundary';
import { useRiskMapTab } from './useRiskMapTab';
import type { RiskMapLinkOptions } from './links';
import { RiskMapLinkOptionsContext } from './linkOptions';
import { lazyWithChunkReload } from './lazyWithChunkReload';

// Each tab is its own chunk: the 3D views pull in three.js, which the rest of
// the app never loads. lazyWithChunkReload recovers from a stale chunk after
// a deploy (KIRIN-FRONTEND-1V) by reloading once instead of erroring.
const RiskFlow = lazyWithChunkReload(() => import('./RiskFlow'));
const BlastRadius = lazyWithChunkReload(() => import('./BlastRadius'));
const AgentRadar = lazyWithChunkReload(() => import('./AgentRadar'));

const TABS: { value: RiskMapTab; label: string }[] = [
  { value: 'flow', label: 'Risk Flow' },
  { value: 'blast', label: 'Blast Radius' },
  { value: 'radar', label: 'Agent Radar' },
];

export interface RiskMapTabProps {
  organizationId: string;
  from: string;
  to: string;
}

function TabFallback(): ReactElement {
  return <Skeleton className="h-[640px] w-full rounded-xl" />;
}

/**
 * KIR-5075: the AI Risk Map area at the top of the dashboard (behind
 * `ai_risk_map`). Three tabs, Risk Flow by default, the selected tab kept in
 * `?tab=`. Uses the dashboard's standard time frame selector; every tab reads
 * the same range.
 */
export function RiskMapSection(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: organization } = useOrganization(organizationId);
  const [tab, setTab] = useRiskMapTab();
  // Link targets that sit behind their own flags (see RiskMapLinkOptions).
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.PROJECTS_INVENTORY,
    flagsmithFeatureFlags.HOOK_INVENTORY_ENABLED,
    flagsmithFeatureFlags.SHADOW_AI,
    flagsmithFeatureFlags.SKILLS_INVENTORY,
  ]);
  const projectsInventoryEnabled =
    flags[flagsmithFeatureFlags.PROJECTS_INVENTORY] ?? false;
  const hookInventoryEnabled =
    flags[flagsmithFeatureFlags.HOOK_INVENTORY_ENABLED] ?? false;
  const shadowAiEnabled = flags[flagsmithFeatureFlags.SHADOW_AI] ?? false;
  const skillsInventoryEnabled =
    flags[flagsmithFeatureFlags.SKILLS_INVENTORY] ?? false;
  const [timeFilter, setTimeFilter] = useDashboardTimeFilter();
  // Whole minutes, so the same preset opened again within a minute (or by
  // another admin) reuses the react-query and server cache entries.
  const { from, to } = quantiseRiskMapRange(
    timeFilter.range.from,
    timeFilter.range.to
  );
  const linkOptions = useMemo<RiskMapLinkOptions>(
    () => ({
      projectsInventoryEnabled,
      hookInventoryEnabled,
      shadowAiEnabled,
      skillsInventoryEnabled,
      range: { from, to },
    }),
    [
      projectsInventoryEnabled,
      hookInventoryEnabled,
      shadowAiEnabled,
      skillsInventoryEnabled,
      from,
      to,
    ]
  );

  if (!organizationId) return null;

  const props: RiskMapTabProps = { organizationId, from, to };

  return (
    <DashboardErrorBoundary>
      <RiskMapLinkOptionsContext.Provider value={linkOptions}>
        <section
          aria-labelledby="ai-risk-map-heading"
          data-testid="ai-risk-map-section"
          className="space-y-4"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 id="ai-risk-map-heading" className="text-lg font-semibold">
                AI Risk Map
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                How AI risk moves through your organization: who triggers it,
                what it can reach, and what agents are doing now.
              </p>
            </div>
            <TimeRangeSelector
              timeFilter={timeFilter}
              onChange={setTimeFilter}
              minDate={organization?.createdAt}
            />
          </div>

          <Tabs value={tab} onValueChange={v => setTab(v as RiskMapTab)}>
            <TabsList>
              {TABS.map(t => (
                <TabsTrigger key={t.value} value={t.value}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <Suspense fallback={<TabFallback />}>
              <TabsContent value="flow">
                {tab === 'flow' && <RiskFlow {...props} />}
              </TabsContent>
              <TabsContent value="blast">
                {tab === 'blast' && <BlastRadius {...props} />}
              </TabsContent>
              <TabsContent value="radar">
                {tab === 'radar' && <AgentRadar {...props} />}
              </TabsContent>
            </Suspense>
          </Tabs>
        </section>
      </RiskMapLinkOptionsContext.Provider>
    </DashboardErrorBoundary>
  );
}
